"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupConnection = exports.getFinalConnection = exports.getBracketConnection = exports.addParticipantImage = exports.addParticipantOrigin = exports.setupLoss = exports.setupWin = exports.setupBye = exports.setupHint = exports.createRankingHeaders = exports.createCell = exports.createRow = exports.createTable = exports.createResultContainer = exports.createNameContainer = exports.createParticipantContainer = exports.createOpponentsContainer = exports.createChildCountLabel = exports.createMatchLabel = exports.createMatchContainer = exports.createRoundContainer = exports.createRoundsContainer = exports.createGroupContainer = exports.createBracketContainer = exports.createEliminationContainer = exports.createRoundRobinContainer = exports.createPopoverTitle = exports.createTitle = void 0;
const helpers_1 = require("./helpers");
const lang_1 = require("./lang");
/**
 * Creates the title of the viewer.
 *
 * @param title The title to set.
 */
function createTitle(title) {
    const h1 = document.createElement('h1');
    h1.innerText = title;
    return h1;
}
exports.createTitle = createTitle;
/**
 * Creates the title of a popover.
 *
 * @param title The title to set.
 */
function createPopoverTitle(title) {
    const h4 = document.createElement('h4');
    h4.innerText = title;
    return h4;
}
exports.createPopoverTitle = createPopoverTitle;
/**
 * Creates a container which contains a round-robin stage.
 *
 * @param stageId ID of the stage.
 */
function createRoundRobinContainer(stageId) {
    const stage = document.createElement('div');
    stage.classList.add('round-robin');
    stage.setAttribute('data-stage-id', stageId.toString());
    return stage;
}
exports.createRoundRobinContainer = createRoundRobinContainer;
/**
 * Creates a container which contains an elimination stage.
 *
 * @param stageId ID of the stage.
 */
function createEliminationContainer(stageId) {
    const stage = document.createElement('div');
    stage.classList.add('elimination');
    stage.setAttribute('data-stage-id', stageId.toString());
    return stage;
}
exports.createEliminationContainer = createEliminationContainer;
/**
 * Creates a container which contains one bracket of a single or double elimination stage.
 *
 * @param groupId ID of the group.
 * @param title Title of the group.
 * @param roundCount The number of rounds in this container. This is added as a dataset
 * key 'round-count' so that it can be targeted with CSS.
 */
function createBracketContainer(groupId, title, roundCount) {
    const bracket = document.createElement('section');
    bracket.classList.add('bracket');
    if (roundCount)
        bracket.setAttribute('data-round-count', roundCount.toString());
    // Consolation matches are not in a group.
    if (groupId)
        bracket.setAttribute('data-group-id', groupId.toString());
    if (title) {
        const h2 = document.createElement('h2');
        h2.innerText = title;
        bracket.append(h2);
    }
    return bracket;
}
exports.createBracketContainer = createBracketContainer;
/**
 * Creates a container which contains a group for round-robin stages.
 *
 * @param groupId ID of the group.
 * @param title Title of the group.
 */
function createGroupContainer(groupId, title) {
    const h2 = document.createElement('h2');
    h2.innerText = title;
    const group = document.createElement('section');
    group.classList.add('group');
    group.setAttribute('data-group-id', groupId.toString());
    group.append(h2);
    return group;
}
exports.createGroupContainer = createGroupContainer;
/**
 * Creates a container which contains a list of rounds.
 */
function createRoundsContainer() {
    const round = document.createElement('div');
    round.classList.add('rounds');
    return round;
}
exports.createRoundsContainer = createRoundsContainer;
/**
 * Creates a container which contains a round.
 *
 * @param roundId ID of the round.
 * @param title Title of the round.
 */
function createRoundContainer(roundId, title) {
    const h3 = document.createElement('h3');
    h3.innerText = title;
    const round = document.createElement('article');
    round.classList.add('round');
    round.setAttribute('data-round-id', roundId.toString());
    round.append(h3);
    return round;
}
exports.createRoundContainer = createRoundContainer;
/**
 * Creates a container which contains a match.
 *
 * @param match A match or a match game.
 * @param classList Optional extra classes to be added.
 * @param style Optional extra style to be set.
 */
function createMatchContainer(match, classList = null, style) {
    const div = document.createElement('div');
    div.classList.add('match');
    if (match) {
        if ((0, helpers_1.isMatchGame)(match))
            div.setAttribute('data-match-game-id', match.id.toString());
        else
            div.setAttribute('data-match-id', match.id.toString());
        div.setAttribute('data-match-status', match.status.toString());
    }
    addExtraStyles(div, classList, style);
    return div;
}
exports.createMatchContainer = createMatchContainer;
/**
 * Creates a container which contains the label of a match.
 *
 * @param label The label of the match.
 * @param status The status to set as tooltip.
 * @param onClick Called when the label is clicked.
 */
function createMatchLabel(label, status, onClick) {
    const span = document.createElement('span');
    span.innerText = label || '';
    span.title = status;
    onClick && span.addEventListener('click', onClick);
    return span;
}
exports.createMatchLabel = createMatchLabel;
/**
 * Creates a container which contains the child count label of a match.
 *
 * @param label The child count label of the match.
 * @param onClick Called when the label is clicked.
 */
function createChildCountLabel(label, onClick) {
    const span = document.createElement('span');
    span.innerText = label;
    onClick && span.addEventListener('click', onClick);
    return span;
}
exports.createChildCountLabel = createChildCountLabel;
/**
 * Creates a container which contains the opponents of a match.
 *
 * @param classList Optional extra classes to be added.
 * @param style Optional extra style to be set.
 * @param onClick Called when the match is clicked.
 */
function createOpponentsContainer(classList = null, style, onClick) {
    const opponents = document.createElement('div');
    opponents.classList.add('opponents');
    onClick && opponents.addEventListener('click', onClick);
    addExtraStyles(opponents, classList, style);
    return opponents;
}
exports.createOpponentsContainer = createOpponentsContainer;
/**
 * Creates a container which contains a participant.
 *
 * @param participantId ID of the participant.
 * @param classList Optional extra classes to be added.
 * @param style Optional extra style to be set.
 */
function createParticipantContainer(participantId, classList = null, style) {
    const participant = document.createElement('div');
    participant.classList.add('participant');
    if (participantId !== null && participantId !== undefined)
        participant.setAttribute('data-participant-id', participantId.toString());
    addExtraStyles(participant, classList, style);
    return participant;
}
exports.createParticipantContainer = createParticipantContainer;
/**
 * Creates a container which contains the name of a participant.
 */
function createNameContainer() {
    const name = document.createElement('div');
    name.classList.add('name');
    return name;
}
exports.createNameContainer = createNameContainer;
/**
 * Creates a container which contains the result of a match for a participant.
 */
function createResultContainer() {
    const result = document.createElement('div');
    result.classList.add('result');
    return result;
}
exports.createResultContainer = createResultContainer;
/**
 * Add the given extra CSS classes and styles to the given element.
 *
 * @param el The element.
 * @param classList Optional extra classes to be added.
 * @param style Optional extra style to be set.
 */
function addExtraStyles(el, classList = null, style) {
    if (Array.isArray(classList)) {
        for (const cls of classList)
            el.classList.add(cls);
    }
    else if (classList)
        el.classList.add(classList);
    if (style) {
        for (const k in style) {
            const v = style[k];
            if (v)
                el.style[k] = v;
        }
    }
}
/**
 * Creates a table.
 */
function createTable() {
    return document.createElement('table');
}
exports.createTable = createTable;
/**
 * Creates a table row.
 */
function createRow() {
    return document.createElement('tr');
}
exports.createRow = createRow;
/**
 * Creates a table cell.
 *
 * @param data The data in the cell.
 */
function createCell(data) {
    const td = document.createElement('td');
    td.innerText = String(data);
    return td;
}
exports.createCell = createCell;
/**
 * Creates the headers for a ranking table.
 *
 * @param ranking The object containing the ranking.
 */
function createRankingHeaders(ranking) {
    const headers = document.createElement('tr');
    const firstItem = ranking[0];
    for (const key in firstItem) {
        const prop = key;
        const header = (0, helpers_1.rankingHeader)(prop);
        const th = document.createElement('th');
        th.innerText = header.text;
        th.setAttribute('title', header.tooltip);
        headers.append(th);
    }
    return headers;
}
exports.createRankingHeaders = createRankingHeaders;
/**
 * Sets a hint on a name container.
 *
 * @param nameContainer The name container.
 * @param hint The hint to set.
 * @param withoutTitle True to not add the title to the element (i.e. the
 * browser-driven tooltip).
 */
function setupHint(nameContainer, hint, withoutTitle = false) {
    nameContainer.classList.add('hint');
    nameContainer.innerText = hint;
    if (!withoutTitle)
        nameContainer.title = hint;
}
exports.setupHint = setupHint;
/**
 * Sets a BYE on a name container.
 *
 * @param nameContainer The name container.
 */
function setupBye(nameContainer) {
    nameContainer.innerText = (0, lang_1.t)('common.bye');
    nameContainer.classList.add('bye');
}
exports.setupBye = setupBye;
/**
 * Sets a win for a participant.
 *
 * @param participantContainer The participant container.
 * @param resultContainer The result container.
 * @param participant The participant result.
 */
function setupWin(participantContainer, resultContainer, participant) {
    if (participant.result && participant.result === 'win') {
        participantContainer.classList.add('win');
        if (participant.score === undefined)
            resultContainer.innerText = (0, lang_1.t)('abbreviations.win');
    }
}
exports.setupWin = setupWin;
/**
 * Sets a loss for a participant.
 *
 * @param participantContainer The participant container.
 * @param resultContainer The result container.
 * @param participant The participant result.
 */
function setupLoss(participantContainer, resultContainer, participant) {
    if (participant.result && participant.result === 'loss' || participant.forfeit) {
        participantContainer.classList.add('loss');
        if (participant.forfeit)
            resultContainer.innerText = (0, lang_1.t)('abbreviations.forfeit');
        else if (participant.score === undefined)
            resultContainer.innerText = (0, lang_1.t)('abbreviations.loss');
    }
}
exports.setupLoss = setupLoss;
/**
 * Adds the participant origin to a name.
 *
 * @param nameContainer The name container.
 * @param text The text to set (origin).
 * @param placement The placement of the participant origin.
 */
function addParticipantOrigin(nameContainer, text, placement) {
    const span = document.createElement('span');
    span.classList.add('origin');
    if (placement === 'before') {
        span.innerText = `${text} `;
        nameContainer.prepend(span);
    }
    else if (placement === 'after') {
        span.innerText = ` (${text})`;
        nameContainer.append(span);
    }
}
exports.addParticipantOrigin = addParticipantOrigin;
/**
 * Adds the participant image to a name.
 *
 * @param nameContainer The name container.
 * @param src Source of the image.
 */
function addParticipantImage(nameContainer, src) {
    const img = document.createElement('img');
    img.src = src;
    nameContainer.prepend(img);
}
exports.addParticipantImage = addParticipantImage;
/**
 * Returns the connection for a given round in a bracket.
 *
 * @param alwaysConnectFirstRound Whether to always connect the first round with the second round.
 * @param roundNumber Number of the round.
 * @param roundCount Count of rounds.
 * @param match The match to connect to other matches.
 * @param matchLocation Location of the match.
 * @param connectFinal Whether to connect to the final.
 */
function getBracketConnection(alwaysConnectFirstRound, roundNumber, roundCount, match, matchLocation, connectFinal) {
    var _a, _b, _c;
    const connection = {
        connectPrevious: false,
        connectNext: false,
    };
    if (matchLocation === 'loser_bracket') {
        connection.connectPrevious = roundNumber > 1 && (roundNumber % 2 === 1 ? 'square' : 'straight');
        connection.connectNext = roundNumber < roundCount && (roundNumber % 2 === 0 ? 'square' : 'straight');
    }
    else {
        connection.connectPrevious = roundNumber > 1 && 'square';
        connection.connectNext = roundNumber < roundCount ? 'square' : (connectFinal ? 'straight' : false);
    }
    if (alwaysConnectFirstRound || roundNumber !== 2)
        return connection;
    const upperBracket = matchLocation === 'single_bracket' || matchLocation === 'winner_bracket';
    if (upperBracket && ((_a = match.opponent1) === null || _a === void 0 ? void 0 : _a.position) === undefined && ((_b = match.opponent2) === null || _b === void 0 ? void 0 : _b.position) === undefined)
        connection.connectPrevious = false;
    if (matchLocation === 'loser_bracket' && ((_c = match.opponent2) === null || _c === void 0 ? void 0 : _c.position) === undefined)
        connection.connectPrevious = false;
    return connection;
}
exports.getBracketConnection = getBracketConnection;
/**
 * Returns the connection for a given round in the final.
 *
 * @param finalType Type of final.
 * @param roundNumber Number of the round.
 * @param matchCount The count of matches.
 */
function getFinalConnection(finalType, roundNumber, matchCount) {
    return {
        connectPrevious: finalType === 'grand_final' && (roundNumber === 1 && 'straight'),
        connectNext: matchCount === 2 && roundNumber === 1 && 'straight',
    };
}
exports.getFinalConnection = getFinalConnection;
/**
 * Sets the connection a match containers.
 *
 * @param opponentsContainer The opponents container.
 * @param matchContainer The match container.
 * @param connection The connection to set.
 */
function setupConnection(opponentsContainer, matchContainer, connection) {
    if (connection.connectPrevious)
        opponentsContainer.classList.add('connect-previous');
    if (connection.connectNext)
        matchContainer.classList.add('connect-next');
    if (connection.connectPrevious === 'straight')
        opponentsContainer.classList.add('straight');
    if (connection.connectNext === 'straight')
        matchContainer.classList.add('straight');
}
exports.setupConnection = setupConnection;
